"use client";

import { useSyncExternalStore } from "react";
import { useIsTouchDevice } from "./use-is-touch-device";

/**
 * Remembered per device: once a hardware keyboard has spoken, it has one.
 * A detached iPad keyboard leaves the hints on — a device that HAS a
 * keyboard is the case the hints exist for, and the browser offers no way
 * to hear it leave.
 */
const STORAGE_KEY = "ui:hardware-keyboard";

/**
 * Keys a soft keyboard never emits. A printable key, Enter and Backspace all
 * arrive from the on-screen keyboard too, so they prove nothing; a modifier,
 * Tab, Escape, an arrow, a function key or a page key proves the keyboard is
 * a real one.
 */
export function isHardwareKey(event: KeyboardEvent): boolean {
  const { key } = event;
  if (event.metaKey || event.ctrlKey || event.altKey) return true;
  switch (key) {
    case "Meta":
    case "Control":
    case "Alt":
    case "Tab":
    case "Escape":
    case "ArrowUp":
    case "ArrowDown":
    case "ArrowLeft":
    case "ArrowRight":
    case "Home":
    case "End":
    case "PageUp":
    case "PageDown":
      return true;
    default:
      return /^F\d{1,2}$/.test(key);
  }
}

let seen: boolean | null = null;
const listeners = new Set<() => void>();

function readSeen(): boolean {
  if (seen === null) {
    try {
      seen = window.localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      seen = false;
    }
  }
  return seen;
}

function onKeyDown(event: KeyboardEvent): void {
  if (seen === true || !isHardwareKey(event)) return;
  seen = true;
  try {
    window.localStorage.setItem(STORAGE_KEY, "1");
  } catch {
    // Private mode or a full store: the flag lives for this load only.
  }
  for (const listener of listeners) listener();
}

// One window listener for every subscriber, attached by the first and
// detached by the last — never at module scope (see the library's `document`
// rule: the barrel is imported by non-DOM consumers).
function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1) {
    window.addEventListener("keydown", onKeyDown, true);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      window.removeEventListener("keydown", onKeyDown, true);
    }
  };
}

function getServerSnapshot(): boolean {
  return false;
}

/**
 * Whether this device can press the shortcuts the UI would advertise.
 *
 * The web has no "hardware keyboard present" signal, and touch is not its
 * absence — an iPad with a keyboard reports a coarse pointer and presses ⌘K.
 * So the answer is behavioural, the way iPadOS shows its own hints only once
 * a keyboard is attached: a fine pointer is assumed to come with a keyboard;
 * a coarse one is assumed not to, until a key a soft keyboard cannot produce
 * (a modifier, Tab, Escape, an arrow) is heard — then the device is
 * remembered as keyboarded.
 *
 * Every keycap in the library (`Kbd`, `MenuShortcut`, `ContextMenuShortcut`,
 * `CommandShortcut`, a toast's action keys) reads this and renders nothing
 * when it is false, so a call site never gates a keycap itself. A surface
 * that is ONLY about shortcuts — a legend, a "Keyboard shortcuts" row —
 * reads it directly and stands down as a whole.
 */
export function useKeyboardHints(): boolean {
  const isTouch = useIsTouchDevice();
  const keyboardSeen = useSyncExternalStore(
    subscribe,
    readSeen,
    getServerSnapshot,
  );
  return !isTouch || keyboardSeen;
}
